import Heart from '@components/icons/Heart'
import HeartSelected from '@components/icons/HeartSelected'
import { useAddToCartFromPlp } from '@pylot-data/hooks/cartManager/use-add-to-cart-plp'
import { getProductConfigurableBySku } from '@pylot-data/api/operations/get-product'
import { isSearchConfigurableCandidate } from '@pylot-data/hooks/search/utils/isSearchConfigurableCandidate'
import { reportSynonymConversion } from '@pylot-data/hooks/search/synonymConversion'
import { usePrice } from '@corratech/pylot-price'
import { useDataLayerAction } from '@corratech/pylot-tag-manager'
import { Button } from '@corratech/pylot-ui'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { isGiftCardMixCartResult } from '@corratech/pylot-cart-manager'
import {
    DisabledValues,
    getDisabledOptionValues
} from '@corratech/pylot-variant-selector/src/utils/getDisabledOptionValues'
import { formatCurrency } from '@lib/utils/formatCurrency'
import { isShopTheLookProduct } from '@lib/utils/isShopTheLookProduct'
import { AddToWishlistPlp } from '@corratech/pylot-wishlist/src/AddToWishlist'
import { useElementOnScreen } from '@lib/hooks/useElementOnScreen'
import { useIsMobileScreen, useIsTabletScreen } from '@lib/utils/mediaQuery'
import {
    getVariantBySelectedOptions,
    useProductUI
} from '@pylot-data/hooks/product/use-product-ui'
import {
    CategoryTree,
    ConfigurableAttributeOption,
    ConfigurableProduct,
    ConfigurableProductOptions,
    ConfigurableVariant,
    Maybe,
    ProductStockStatus
} from '@pylot-data/pylotschema'
import { BundleItem } from '@pylot-data/fwrdschema'
import cn from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { rememberPlpProductClick } from '@lib/hooks/cagegory/plp/usePLPScrollRestore'
import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import ConfigurableAddToCart from '../ConfigurableAddToCart'
import { AssociatedProductThumbs } from '../AssociatedProducts/AssociatedProductThumbs'
import { ProductPrice } from '../ProductPrice/ProductPrice'
import MobileImageSlider from './MobileImageSlider'
import s from './ProductTile.module.scss'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { ReviewStars } from '@components/Reviews/ReviewStars'
import {
    SelectedOptions,
    VariantSelector
} from '@corratech/pylot-variant-selector'
import { useRouter } from 'next/router'
import { GiftCardProductPrice } from '../GiftCardProductPrice/GiftCardProductPrice'

export { LoaderTile } from './LoaderTile'

const categoryUrlSuffix = '.html'

/**
 * Completa el `price_range` que viene en `associated_products`, mas pobre que el
 * de `ProductItemFragment`: no trae `__typename` ni `maximum_price`.
 *
 * Los dos faltantes tumbaban la PLP entera al cambiar de color:
 *
 * - sin `__typename`, `usePrice` lanza, porque lo usa de discriminador
 *   (lib/casaideas-components/pylot-price/src/hooks/usePrice.ts:47);
 * - sin `maximum_price`, `ProductPrice` hace `formatCurrency(String(undefined))`
 *   -> `parseFloat('undefined')` -> NaN -> throw (ProductPrice.tsx:43 y
 *   lib/utils/formatCurrency.ts:38). Ese camino se activa con
 *   `shouldDisplayPriceRange`, que el tile pasa fijo para los configurables: o
 *   sea, justo para los colores hermanos.
 *
 * `maximum_price` cae al minimo en vez de quedar vacio: `ProductPrice` compara
 * ambos extremos para decidir si pinta un rango, asi que con un maximo vacio
 * saldria "$34.990 - " con el guion colgando. Igualandolo se ve un precio unico,
 * y el paso completo trae el rango real un instante despues.
 *
 * Se normaliza aca y no en el fragmento por dos razones: el tile sabe que lo que
 * construye es un PriceRange, y asi el arreglo no depende de un deploy del
 * middleware.
 */
const withCompletePriceRange = (product: any): any => {
    const priceRange = product?.price_range

    if (!priceRange) {
        return product
    }

    const minimum = priceRange.minimum_price

    return {
        ...product,
        price_range: {
            ...priceRange,
            __typename: priceRange.__typename ?? 'PriceRange',
            ...(priceRange.maximum_price || !minimum
                ? {}
                : {
                      maximum_price: {
                          __typename: 'ProductPrice',
                          final_price: minimum.final_price,
                          regular_price: minimum.regular_price
                      }
                  })
        }
    }
}

/** Los SKU de Casaideas llegan en mayusculas desde Magento y en minusculas desde algunas URLs. */
const sameSku = (a?: string | null, b?: string | null): boolean =>
    typeof a === 'string' &&
    typeof b === 'string' &&
    a.trim().toUpperCase() === b.trim().toUpperCase()
const NEW_FLAG_STRING = 'NUEVO'
const GIFT_CARD_TYPENAME = 'GiftCardProduct'
const GIFTCARD_CAROUSEL = '/account/gift-card'

export interface ProductTilePlpProps {
    product: any
    imageParam: {
        width?: number
    }
    className?: string
    isPDP?: boolean
    isFBT?: boolean
    handleChangeItemOption?: any
    category?: Maybe<CategoryTree>
    lowStockBadgeVisible?: boolean
    lowStockBadgeLabel?: string
    /** Si true, se permite mostrar video en el tile (grilla 4 y 7 en PLP) */
    enableVideo?: boolean
    /** Evita el slider de imagen mobile (p. ej. tiles dentro de react-slick) */
    isInCarousel?: boolean
}

export interface Attribute {
    __typename: string
    code: string
    value_index: number
    label: string
}

export interface SmallImage {
    __typename: string
    url: string
}

export interface VariantProduct {
    __typename: string
    id: number
    small_image: SmallImage
    sku: string
    stock_status: string
}

export interface Variant {
    __typename: string
    attributes: Attribute[]
    product: VariantProduct
}

const renderWishListButtonContent = () => {
    return function WishlistButtonContent({
        isProductInWishlist
    }: {
        isProductInWishlist: boolean
    }) {
        return (
            <div className={s['add-to-wishlist-btn']}>
                {isProductInWishlist ? <HeartSelected /> : <Heart />}
            </div>
        )
    }
}

// normalizador de product
const asProduct = (x: any) => {
    if (!x) return null
    if (typeof x === 'object') {
        if (typeof x.sku === 'string' && x.sku.length) return x
        if (x.product && typeof x.product?.sku === 'string') return x.product
    }
    return null
}

const fullImageUrl = (
    imageUrl?: string,
    imageParam?: { width?: number }
): string => {
    if (!imageUrl) return ''
    const widthString = imageParam?.width ? `&width=${imageParam.width}` : ''
    return `${imageUrl}?auto=webp&format=pjpg${widthString}`
}

export const FwrdProductTilePlp = (
    props: ProductTilePlpProps
): ReactElement | null => {
    const product = asProduct(props.product)
    if (!product) return null
    return <FwrdProductTilePlpContent {...props} product={product} />
}

const FwrdProductTilePlpContent = ({
    product: productProp,
    imageParam = {},
    className,
    isPDP = false,
    isFBT = false,
    category,
    lowStockBadgeVisible = false,
    lowStockBadgeLabel = '',
    handleChangeItemOption = {},
    enableVideo = false,
    isInCarousel = false
}: ProductTilePlpProps & {
    product: NonNullable<ReturnType<typeof asProduct>>
}): ReactElement => {
    const [enrichedProduct, setEnrichedProduct] = useState<any>(null)
    const [selectedSibling, setSelectedSibling] = useState<any>(null)
    const [shouldAutoOpenConfigDrawer, setShouldAutoOpenConfigDrawer] =
        useState(false)
    const configFetchRef = useRef<Promise<any | null> | null>(null)
    const siblingFetchRef = useRef<string | null>(null)

    // Prefer grid-enriched props (batch prefetch) so hover matches PLP latency.
    const productPropHasOptions =
        Array.isArray(productProp?.configurable_options) &&
        productProp.configurable_options.length > 0
    const baseProduct = productPropHasOptions
        ? productProp
        : (enrichedProduct ?? productProp)

    /**
     * EVO-263: al elegir un color en la PLP/SRP se cambia el producto del tile en
     * lugar de navegar a su PDP. Todo lo que el tile pinta sale de `product`
     * —nombre, imagen, precio, URL, tallas, carrito—, asi que sustituirlo aqui
     * hace que la tarjeta entera se recargue sin tocar la PLP.
     */
    const product = selectedSibling ?? baseProduct

    useEffect(() => {
        setEnrichedProduct(null)
        setSelectedSibling(null)
        setShouldAutoOpenConfigDrawer(false)
        configFetchRef.current = null
        siblingFetchRef.current = null
    }, [productProp?.sku])

    const {
        url_key,
        media_gallery,
        badge_image_text: badgeImageText,
        badge_caption_icon: badgeCaptionIcon,
        price_range: productPriceRange,
        customTextAfterProductImage,
        flag,
        new_to_date: newToDate,
        rating_summary,
        review_count,
        ppum,
        is_set,
        small_image
    } = product

    const { t } = useTranslation(['pdp', 'common', 'wishlist'])
    const [hoveredInstantAdd, setHoveredInstantAdd] = useState(false)
    const [isAddToCartDisabled, setIsAddToCartDisabled] = useState(false)
    const [isAddingToCart, setIsAddingToCart] = useState(false)
    const [disabledOptions, setDisabledOptions] =
        useState<DisabledValues | null>(null)
    const isMobile = useIsMobileScreen()
    const isTablet = useIsTabletScreen()
    const { openSidebar, openCartToast, openToast, closeSidebar } = useUI()
    const { addToCart } = useAddToCartFromPlp()

    const { total: productTotalPrice } = usePrice(
        productPriceRange ?? undefined
    )
    const dataLayerAction = useDataLayerAction()
    const search =
        typeof window !== 'undefined' && !isPDP
            ? new URL(window.location.href).search
            : ''

    const [elementRef, isVisible] = useElementOnScreen()
    const isGrouped = product.__typename === 'GroupedProduct'
    const isShopTheLook = isShopTheLookProduct(product)
    const typeName = product.__typename
    const isConfig = typeName === 'ConfigurableProduct'
    const isBundle = typeName === 'BundleProduct'
    const isSearch = productProp.__typename === 'ProductSearchProduct'

    const sku = typeof product.sku === 'string' ? product.sku : ''
    const isConfigSearch = isSearchConfigurableCandidate(productProp)
    const isGiftCardSearch = isSearch && sku.toLowerCase().includes('gift')
    const hasConfigurableOptions =
        Array.isArray(product.configurable_options) &&
        product.configurable_options.length > 0

    const router = useRouter()
    const locale = router.locale || 'es'
    /**
     * EVO-263: el tile se usa igual en PLP y en SRP, y cada contexto tiene su
     * propio tope de miniaturas de color en el storeConfig. El SRP se detecta
     * por la ruta o por el typename que devuelve Live Search.
     */
    const routerPathname = router.pathname ?? ''
    const isSearchResultsContext =
        isSearch ||
        routerPathname === '/search' ||
        routerPathname.endsWith('/search')
    const rawCategoryPath =
        router.asPath?.split('/categoria/')[1] ?? router.asPath ?? ''
    const urlPath = rawCategoryPath
        .split('?')[0]
        .split('#')[0]
        .replace(/\/$/, '')
        .replace(/\.html$/, '')
    const isGiftCardPlp = /gift-?card/i.test(urlPath)
    const isGiftCardProduct =
        product.__typename === GIFT_CARD_TYPENAME ||
        Boolean(product.giftcard_type) ||
        isGiftCardSearch ||
        isGiftCardPlp ||
        urlPath === GIFTCARD_CAROUSEL ||
        router.asPath?.startsWith(GIFTCARD_CAROUSEL)

    const getPlpCtaLabel = (isBusy: boolean, isDisabled: boolean) => {
        if (isBusy) return t('common||Adding...')
        if (isDisabled) return t('common||Out of Stock')
        if (isShopTheLook || isGiftCardProduct) {
            return t('common||pdp|View details').toUpperCase()
        }
        return t('common||pdp|Quick add')
    }

    const getRealPath = () => {
        if (isGiftCardPlp || router.asPath?.startsWith(GIFTCARD_CAROUSEL)) {
            return '/giftcard/detail/'
        }
        return '/producto/'
    }

    const productUrl = `${getRealPath()}${url_key}${categoryUrlSuffix}${
        isConfig ? search : ''
    }`

    const sizeCategoryOption: ConfigurableProductOptions | null =
        product.configurable_options?.find(
            (el: { attribute_code: string }) => el.attribute_code === 'talla'
        ) || null

    const swatchAttr = 'color'

    const {
        setSelectedOptions,
        selectedOptions,
        options,
        variant,
        selectedOptionUIDs,
        isConfigOptionSelected
    } = useProductUI(product)

    const { price_range: variantPriceRange } = variant ?? {}

    const [image, setImage] = useState(
        fullImageUrl(small_image?.url, imageParam)
    )

    /**
     * `image` es estado y el producto del tile puede cambiar sin que el componente
     * se desmonte (swap de color), asi que hay que resincronizarlo o se queda la
     * foto del color anterior. Se depende de `imageParam?.width` y no del objeto,
     * que el padre recrea en cada render.
     */
    useEffect(() => {
        setImage(fullImageUrl(small_image?.url, imageParam))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [small_image?.url, imageParam?.width])

    /**
     * La fila de miniaturas representa al GRUPO, e incluye al producto original:
     * `associated_products` nunca se lista a si mismo, asi que sin esto, despues de
     * cambiar de color no habria forma de volver al que venia en la PLP.
     *
     * Se arma desde `productProp` —no desde `product`— para que la fila no se
     * reordene ni cambie de contenido al ir saltando entre colores.
     */
    const colorGroup = useMemo(() => {
        const siblings = productProp?.associated_products ?? []
        return siblings.length ? [productProp, ...siblings] : []
    }, [productProp])

    /**
     * Cambia el producto del tile. Primero pinta con lo que ya trae la miniatura
     * (nombre, imagen, precio, stock) para que el cambio sea inmediato, y despues
     * completa con la consulta por SKU, que trae tallas y variantes y esta cacheada
     * por sesion. Si la respuesta llega tarde y el usuario ya eligio otro color, se
     * descarta.
     */
    const handleSelectSibling = useCallback(
        async (sibling: any) => {
            const targetSku =
                typeof sibling?.sku === 'string' ? sibling.sku : ''
            if (!targetSku) return

            if (sameSku(targetSku, productProp?.sku)) {
                // Volver al original: `productProp` ya viene completo desde la PLP.
                siblingFetchRef.current = null
                setSelectedSibling(null)
                return
            }

            siblingFetchRef.current = targetSku
            setSelectedSibling(withCompletePriceRange({ ...sibling }))

            try {
                const full = await getProductConfigurableBySku(
                    targetSku,
                    locale
                )
                if (!full || siblingFetchRef.current !== targetSku) return
                setSelectedSibling((prev: any) =>
                    withCompletePriceRange({ ...(prev ?? {}), ...full })
                )
            } catch {
                // Se queda el cambio optimista: la tarjeta ya muestra el color nuevo.
            }
        },
        [locale, productProp]
    )

    const getSecondaryImage: () => string | undefined = useCallback(() => {
        const [, secondaryImage] = media_gallery || []
        if (secondaryImage?.url) {
            return fullImageUrl(secondaryImage.url, imageParam) as string
        }
        return
    }, [media_gallery, imageParam])

    const colorsImagesMap = new Map()
    if (isConfig && product.variants) {
        product.variants.map((v: Variant) => {
            const colorVariant = v.attributes.find(
                (a: { code: string }) => a?.code === swatchAttr
            )
            const img = v.product.small_image
                ? v.product.small_image.url
                : false
            if (colorVariant && img) {
                colorsImagesMap.set(colorVariant.label, fullImageUrl(img))
            }
        })
    }

    const ensureConfigurableProduct = useCallback(async () => {
        if (hasConfigurableOptions) {
            return product
        }
        if (!sku) {
            return null
        }
        if (configFetchRef.current) {
            return configFetchRef.current
        }

        configFetchRef.current = (async () => {
            try {
                const fetched = await getProductConfigurableBySku(sku, locale)
                if (!fetched) {
                    return null
                }
                const merged = { ...productProp, ...fetched }
                setEnrichedProduct(merged)
                return merged
            } catch {
                return null
            } finally {
                configFetchRef.current = null
            }
        })()

        return configFetchRef.current
    }, [hasConfigurableOptions, product, sku, locale, productProp])

    const revealConfigOptions = useCallback(async () => {
        const fetched = await ensureConfigurableProduct()
        if (!fetched) return false

        const hasTalla = fetched.configurable_options?.some(
            (el: { attribute_code: string }) => el.attribute_code === 'talla'
        )
        if (fetched.__typename !== 'ConfigurableProduct' || !hasTalla) {
            return false
        }

        if (isMobile || isTablet) {
            setShouldAutoOpenConfigDrawer(true)
        } else {
            setHoveredInstantAdd(true)
        }
        return true
    }, [ensureConfigurableProduct, isMobile, isTablet])

    const prefetchConfigOptions = useCallback(() => {
        if (isConfigSearch && !hasConfigurableOptions) {
            void ensureConfigurableProduct()
        }
    }, [isConfigSearch, hasConfigurableOptions, ensureConfigurableProduct])

    // Warm options as soon as the tile is on screen (fallback if batch enrich lags).
    useEffect(() => {
        if (isVisible) {
            prefetchConfigOptions()
        }
    }, [isVisible, prefetchConfigOptions])

    // Desktop: reveal size chips on image hover (PLP parity). Also covers the case
    // where the instant-add overlay appears under the cursor without a button mouseenter.
    const handleImageBlockMouseEnter = useCallback(() => {
        prefetchConfigOptions()
        if (!isMobile && !isTablet) {
            setHoveredInstantAdd(true)
        }
    }, [prefetchConfigOptions, isMobile, isTablet])

    const handleImageBlockMouseLeave = useCallback(() => {
        setHoveredInstantAdd(false)
    }, [])

    const addProductHandler = async (
        newSelectedOptions?: SelectedOptions,
        quantity?: number
    ) => {
        if (isAddingToCart) return
        // Shop the Look / giftcard / grouped → PDP (no instant add)
        if (isShopTheLook || isGrouped || isGiftCardProduct) {
            router.push(productUrl)
            return
        }

        // SRP configurable: fetch options and show selector instead of PDP redirect
        if (isConfigSearch && !hasConfigurableOptions && !newSelectedOptions) {
            const revealed = await revealConfigOptions()
            if (revealed) return
            // Fallback if options cannot be loaded (same as previous SRP behavior)
            router.push(productUrl)
            return
        }

        setIsAddingToCart(true)

        try {
            // 'variant' of a SimpleProduct is the same as 'product'
            // sample product from casaideas magento to test the checkout page
            // TODO: Remove hardcode below when we have the products coming from the API properly
            let newVariant = variant
            let newSelectedOptionUIDs = selectedOptionUIDs
            if (newSelectedOptions) {
                const variantId = getVariantBySelectedOptions(
                    product,
                    newSelectedOptions
                )

                newVariant = product?.variants?.[variantId]?.product ?? variant

                const { attributes } = product?.variants?.[variantId] ?? {}

                newSelectedOptionUIDs =
                    (attributes as ConfigurableAttributeOption[])?.map(
                        (attr) => attr?.uid ?? ''
                    ) ?? []
            }

            const bundleOptions =
                isBundle &&
                product?.items.map(
                    (item: BundleItem) =>
                        item.options?.length && item.options[0]?.uid!
                )

            const addToCartOptions = isBundle
                ? bundleOptions
                : newSelectedOptionUIDs

            const response = await addToCart(
                [
                    {
                        sku: newVariant?.sku!,
                        uid: newVariant?.uid,
                        ...(newVariant?.sku !== product.sku && {
                            parent_sku: product.sku
                        }),
                        selected_options: addToCartOptions,
                        quantity: quantity ? quantity : 1
                    }
                ],
                [product]
            )

            const errorMessage =
                response?.errors?.[0]?.message ??
                response?.user_errors?.[0]?.message ??
                null
            const successMessage = t(
                'common||cart|{{name}} was added to cart',
                {
                    name: product.name
                }
            )

            if (isGiftCardMixCartResult(response)) {
                return
            }

            if (!isMobile && !errorMessage) {
                openSidebar()
                setTimeout(() => {
                    closeSidebar()
                }, 4000)
            } else if (errorMessage) {
                openSidebar()
            }

            if (response?.data && !errorMessage) {
                const { cart } = response.data
                cartUpdateEvent('add', product, cart)
            }

            setTimeout(
                () =>
                    isMobile && !errorMessage
                        ? openToast(
                              errorMessage ? errorMessage : successMessage,
                              errorMessage
                                  ? ToastType.Warning
                                  : ToastType.Success,
                              true
                          )
                        : openCartToast(
                              errorMessage ? errorMessage : successMessage,
                              errorMessage
                                  ? ToastType.Warning
                                  : ToastType.Success
                          ),
                50
            )
        } finally {
            setIsAddingToCart(false)
        }
    }

    useEffect(() => {
        if (product?.variants?.length > 0) {
            const productVariantIds = product.variants?.map(
                (v: ConfigurableVariant) => v?.product?.id
            )
            setIsAddToCartDisabled(!productVariantIds?.includes(variant?.id))
        }
    }, [product?.variants, variant])

    useEffect(() => {
        if (options.length && product.variants) {
            const disabled = getDisabledOptionValues(
                options,
                selectedOptions,
                product.variants
            )
            setDisabledOptions(disabled)
        }
    }, [options, product.variants, selectedOptions])

    const effectivePpum =
        variant?.sku && variant.sku !== product.sku ? variant.ppum : ppum

    const unitPrice = formatCurrency(effectivePpum ? effectivePpum : '')

    const effectiveIsSet =
        variant?.sku && variant.sku !== product.sku ? variant.is_set : is_set

    const { values: sizeOptionValues } = sizeCategoryOption || {}
    const instantAddFilteredValues = sizeOptionValues?.filter(
        (switchOption) => !disabledOptions?.talla.includes(switchOption?.label!)
    )

    const isInstantAddOutOfStock =
        isConfig && instantAddFilteredValues
            ? instantAddFilteredValues.length === 0 && isAddToCartDisabled
            : isAddToCartDisabled

    const isInstantAddDisabled = isInstantAddOutOfStock || isAddingToCart

    // Same PLP hover UX: show size chips when options are ready and button is hovered.
    const showSizeOptions =
        Boolean(hoveredInstantAdd) &&
        Boolean(instantAddFilteredValues) &&
        (instantAddFilteredValues?.length ?? 0) > 0

    const instantAddLabel = getPlpCtaLabel(
        isAddingToCart,
        isInstantAddDisabled && !isGiftCardProduct && !isShopTheLook
    )

    const instantAddToCart = (
        <div
            className={cn(s['productitem-instant-add'], 'reversed-button', {
                [s['instant-add-with-options']]: showSizeOptions
            })}
        >
            <Button
                type="button"
                className={cn('btn-add-to-cart-plp-pdp', {
                    'instant-add-with-options': showSizeOptions
                })}
                aria-label={showSizeOptions ? undefined : instantAddLabel}
                disabled={!showSizeOptions && isInstantAddDisabled}
                onClick={
                    showSizeOptions
                        ? undefined
                        : () => {
                              if (!isInstantAddDisabled) addProductHandler()
                          }
                }
            >
                {showSizeOptions ? (
                    <div className={s['productitem-instant-add-options']}>
                        {instantAddFilteredValues!.map((switchOption) => (
                            <span
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isAddingToCart) {
                                        setSelectedOptions((prevState) => {
                                            const newSelectedOptions = {
                                                ...prevState,
                                                talla: switchOption?.swatch_data
                                                    ?.value!
                                            }
                                            addProductHandler(
                                                newSelectedOptions
                                            )
                                            return newSelectedOptions
                                        })
                                    }
                                }}
                                onClick={() => {
                                    if (isAddingToCart) return

                                    setSelectedOptions((prevState) => {
                                        const newSelectedOptions = {
                                            ...prevState,
                                            talla: switchOption?.swatch_data
                                                ?.value!
                                        }
                                        addProductHandler(newSelectedOptions)
                                        return newSelectedOptions
                                    })
                                }}
                                key={switchOption?.value_index}
                            >
                                {switchOption?.label}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span
                        className={cn({
                            [s['instantadd-button-disabled']]:
                                isInstantAddDisabled
                        })}
                    >
                        <div>{instantAddLabel}</div>
                    </span>
                )}
            </Button>
        </div>
    )

    // handle variant changes on PDP (FBTProducts)
    useEffect(() => {
        if (isFBT && isConfig && product?.variants) {
            let newSelectedOptionUIDs = selectedOptionUIDs
            if (selectedOptions) {
                const variantId = getVariantBySelectedOptions(
                    product,
                    selectedOptions
                )
                const { attributes } = product?.variants?.[variantId] ?? {}
                newSelectedOptionUIDs =
                    (attributes as ConfigurableAttributeOption[])?.map(
                        (attr) => attr?.uid ?? ''
                    ) ?? []
            }
            handleChangeItemOption(product, newSelectedOptionUIDs)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions])

    const pushProductClickEvent = (e?: React.MouseEvent) => {
        const totalCurrency = formatCurrency(productTotalPrice)

        // Posición en el listado: la restauración al volver atrás apunta a este SKU
        rememberPlpProductClick(sku)

        dataLayerAction({
            type: 'PRODUCT_CLICK',
            data: {
                ...product,
                price: parseFloat(
                    totalCurrency
                        .replace('$', '')
                        .replace('.', '')
                        .replace(',', '.')
                ),
                category: category?.name,
                currencyCode:
                    product.price_range?.minimum_price?.final_price?.currency
            }
        })

        // MSE: search product click for Live Search attribution
        if (isSearchResultsContext) {
            const mse = (window as any).magentoStorefrontEvents
            if (mse) {
                mse.publish.searchProductClick('search-bar', sku)
                // Feed behavior_score when this click came from a synonym-driven search.
                void reportSynonymConversion('PRODUCT_CLICK')
            }

            // Prevent default and push manually to ensure event is sent
            if (
                e &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                e.button !== 1
            ) {
                e.preventDefault()
                router.push(productUrl)
            }
        }
    }

    let flagNuevo
    product.categories?.forEach((categoria = Object) => {
        if (categoria && (categoria as any).name === 'Nuevos') {
            flagNuevo = 1
        }
    })

    const badgeLabel = flagNuevo ? NEW_FLAG_STRING : badgeImageText
    const badgeText = flag ? flag.label : badgeLabel
    const showLowStockBadge = lowStockBadgeVisible && !badgeText
    const productBadgeText =
        badgeText || (showLowStockBadge ? lowStockBadgeLabel : '')
    const hasReviews = review_count && review_count > 0

    const reviewStars = hasReviews ? (
        <ReviewStars
            rating={rating_summary}
            numberOfStars={1}
            showAverage
            className={s['tile-rating-inline']}
        />
    ) : null

    const sizeFilteredValues =
        sizeCategoryOption &&
        sizeCategoryOption.values?.filter(
            (switchOption) =>
                !disabledOptions?.talla.includes(switchOption?.label!)
        )

    const hasSizeVariants =
        isConfig && sizeFilteredValues && sizeFilteredValues?.length > 0

    const simpleProductAddToCart = (isMobile || isTablet) && (
        <Button
            onClick={() => addProductHandler()}
            disabled={
                isShopTheLook || isGiftCardProduct
                    ? false
                    : isAddToCartDisabled || isAddingToCart
            }
            className={cn(
                'btn-add-to-cart-plp-pdp',
                s['instant-add-btn-mobile']
            )}
        >
            <h5>
                {getPlpCtaLabel(
                    isAddingToCart,
                    !isShopTheLook && !isGiftCardProduct && isAddToCartDisabled
                )}
            </h5>
        </Button>
    )

    const configurableAddToCart = hasSizeVariants && (
        <ConfigurableAddToCart
            isAddToCartDisabled={isAddToCartDisabled}
            productUrl={productUrl}
            sizeCategoryOption={sizeCategoryOption}
            disabledOptions={disabledOptions}
            product={product}
            addProductHandler={addProductHandler}
            defaultOpen={shouldAutoOpenConfigDrawer}
        />
    )

    const configProductPrice = (
        <ProductPrice
            className={String(typeName).toLowerCase()}
            priceRange={productPriceRange}
            shouldDisplayPriceRange
            asideContent={reviewStars}
            isPlp
        />
    )
    const productPrice = (
        <ProductPrice
            className={String(typeName).toLowerCase()}
            priceRange={variantPriceRange || productPriceRange}
            asideContent={reviewStars}
            isPlp
        />
    )

    const productPriceContainer =
        !isConfig && !isConfigSearch ? productPrice : configProductPrice

    const mobileAddToCartSelector = hasSizeVariants
        ? configurableAddToCart
        : simpleProductAddToCart

    return (
        <div
            data-product-sku={sku}
            id={sku}
            className={cn(
                s['productitem-block'],
                { show: isVisible },
                { [s['has-custom-text']]: !!customTextAfterProductImage },
                className
            )}
            ref={elementRef}
        >
            <div
                className={s['productimage-block']}
                onMouseEnter={handleImageBlockMouseEnter}
                onMouseLeave={handleImageBlockMouseLeave}
            >
                <div
                    className={cn(s['product-badge'], {
                        [s['product-badge-low-stock']]: showLowStockBadge
                    })}
                >
                    {productBadgeText}
                </div>
                <div aria-hidden onClick={pushProductClickEvent}>
                    <Link
                        href={productUrl}
                        className={cn(
                            s['productimg-link'],
                            s['productimg-slider']
                        )}
                        aria-label={product.name}
                        rel="noopener noreferrer"
                    >
                        {isMobile && !isInCarousel ? (
                            <MobileImageSlider
                                productName={product.name}
                                image={image}
                                getSecondaryImage={getSecondaryImage}
                                productUrl={productUrl}
                                productId={sku.toString()}
                                categoryId={
                                    category?.uid?.toString() ||
                                    search.toString()
                                }
                            />
                        ) : (
                            <>
                                <img
                                    src={image}
                                    alt={
                                        product.name
                                            ? product.name
                                            : 'placeholder'
                                    }
                                    width={318}
                                    height={318}
                                    className={cn(s['product-img'], 'contain')}
                                />
                                <img
                                    src={getSecondaryImage() || image}
                                    alt={
                                        product.name
                                            ? product.name
                                            : 'placeholder'
                                    }
                                    width={318}
                                    height={318}
                                    className={cn(
                                        s['productimg-secondary'],
                                        'contain'
                                    )}
                                />
                            </>
                        )}
                    </Link>
                </div>
                {!isPDP && instantAddToCart}
                <AddToWishlistPlp
                    sku={sku}
                    selected_options={selectedOptionUIDs}
                    quantity={1}
                    isWishlistDisabled={isConfigOptionSelected}
                    isConfig={isConfig}
                    enableRemoveProductFromWishlist
                    buttonContent={renderWishListButtonContent()}
                    errorWithToast
                    stayOnPage
                />
            </div>
            {customTextAfterProductImage && (
                <div className={s['product-custom-text']}>
                    {customTextAfterProductImage}
                </div>
            )}
            {badgeCaptionIcon && (
                <div
                    className={s['product-badge-caption']}
                    dangerouslySetInnerHTML={{ __html: badgeCaptionIcon }}
                />
            )}
            <div className={s['product-details']}>
                <div className={s['details-container']}>
                    <h6 className={cn(s['product-name'])}>
                        <div aria-hidden onClick={pushProductClickEvent}>
                            <Link
                                href={productUrl}
                                className={s['productname-link']}
                                title={product.name}
                                rel="noopener noreferrer"
                            >
                                {product.name}
                            </Link>
                        </div>
                    </h6>

                    {product.product_type_label && (
                        <div className={s['product-type']}>
                            {product.product_type_label}
                        </div>
                    )}

                    {product.model_no && (
                        <div className={s['model-no']}>{product.model_no}</div>
                    )}

                    {typeName === GIFT_CARD_TYPENAME ? (
                        <GiftCardProductPrice
                            className={String(typeName).toLowerCase()}
                            minimum_price={
                                product?.giftcard_amounts?.[0]?.value
                            }
                            maximum_price={
                                product?.giftcard_amounts?.[
                                    (product?.giftcard_amounts?.length || 1) - 1
                                ]?.value
                            }
                        />
                    ) : (
                        productPriceContainer
                    )}

                    {/*
                        EVO-263: miniaturas de los colores hermanos, bajo el
                        precio. Cada color es un configurable aparte, por lo que
                        vienen en `associated_products` y no en `variants`; el
                        selector de tallas de abajo queda intacto.

                        El tope sale del storeConfig segun el contexto
                        (`associated_products_srp_limit` en el SRP,
                        `associated_products_plp_limit` en la PLP) y el "+N"
                        despliega el resto dentro de la card. La PLP no lleva
                        titulo (el diseno solo lo muestra en la PDP).
                    */}
                    <AssociatedProductThumbs
                        products={colorGroup}
                        currentSku={sku}
                        context={isSearchResultsContext ? 'srp' : 'plp'}
                        onSelect={handleSelectSibling}
                    />

                    {isConfig && product.variants && (
                        <VariantSelector
                            disableAll={
                                (product as ConfigurableProduct)
                                    .stock_status ===
                                ProductStockStatus.OutOfStock
                            }
                            variants={product.variants as ConfigurableVariant[]}
                            options={options}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            // @ts-ignore
                            setImage={setImage}
                            colorsImagesMap={colorsImagesMap}
                            displayTallaSwatch={false}
                            displaySwatchLabel={false}
                            restriction={7}
                            productURL={productUrl}
                        />
                    )}

                    {effectiveIsSet === 1 &&
                    typeof effectivePpum === 'number' &&
                    !isNaN(effectivePpum) ? (
                        <div className={s['price-per-unit-cont']}>
                            <span>
                                {t('common||pdp||Price per unit')} {unitPrice}
                            </span>
                        </div>
                    ) : null}
                </div>

                {(isMobile || isTablet) && mobileAddToCartSelector}
            </div>
        </div>
    )
}

export const ProductItem = React.memo(
    (props: ProductTilePlpProps) => <FwrdProductTilePlp {...props} />,
    (prevProps, newProps) =>
        JSON.stringify(prevProps) === JSON.stringify(newProps)
)

ProductItem.displayName = 'MemoProductItem'
